// Utility functions for the taxi booking application

/**
 * Format currency in Indian Rupees
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format date and time for display
 * @param {string} date - Date string (YYYY-MM-DD)
 * @param {string} time - Time string (HH:MM)
 * @returns {string} Formatted date and time
 */
export const formatDateTime = (date, time) => {
  const dateObj = new Date(`${date}T${time}`);
  return dateObj.toLocaleString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Calculate fare based on distance, vehicle type, trip type and dates
 * @param {number} distance - Distance in kilometers
 * @param {string} vehicleType - Type of vehicle (sedan, suv, innova)
 * @param {string} tripType - Type of trip (oneway, roundtrip)
 * @param {string} pickupDate - Pickup date (optional)
 * @param {string} returnDate - Return date (optional, for round trips)
 * @returns {object} Fare breakdown
 */
export const calculateFare = (distance, vehicleType, tripType, pickupDate = null, returnDate = null) => {
  const pricing = {
    sedan: { oneWay: 14, roundTrip: 13, driverBata: 400 },
    suv: { oneWay: 19, roundTrip: 18, driverBata: 400 },
    innova: { oneWay: 20, roundTrip: 18, driverBata: 400 }
  };

  if (!pricing[vehicleType]) {
    throw new Error('Invalid vehicle type');
  }

  // Calculate effective distance based on trip type
  let effectiveDistance = distance;
  let rate = pricing[vehicleType].oneWay;
  
  if (tripType === 'roundtrip') {
    // For round trips: double the distance and use round trip rate
    effectiveDistance = distance * 2;
    rate = pricing[vehicleType].roundTrip;
  } else {
    // For one way trips: use one way rate
    rate = pricing[vehicleType].oneWay;
  }
  
  // Calculate number of days for round trips
  let numberOfDays = 1;
  if (tripType === 'roundtrip' && pickupDate && returnDate) {
    const pickup = new Date(pickupDate);
    const returnD = new Date(returnDate);
    const timeDiff = returnD.getTime() - pickup.getTime();
    numberOfDays = Math.ceil(timeDiff / (1000 * 3600 * 24)); // Convert to days
    numberOfDays = Math.max(numberOfDays, 1); // Minimum 1 day
  }
  
  const baseFare = effectiveDistance * rate;
  const driverBata = pricing[vehicleType].driverBata * numberOfDays;
  const totalFare = baseFare + driverBata;

  return { 
    baseFare, 
    totalFare, 
    driverBata, 
    rate, 
    effectiveDistance, 
    oneWayDistance: distance,
    tripType,
    numberOfDays,
    dailyDriverBata: pricing[vehicleType].driverBata
  };
};

/**
 * Validate form data
 * @param {object} formData - Form data to validate
 * @returns {object} Validation result with errors
 */
export const validateForm = (formData) => {
  const errors = {};
  
  if (!formData.pickupLocation?.trim()) {
    errors.pickupLocation = 'Pickup location is required';
  }
  
  if (!formData.dropoffLocation?.trim()) {
    errors.dropoffLocation = 'Drop-off location is required';
  }
  
  if (!formData.pickupDate) {
    errors.pickupDate = 'Pickup date is required';
  }
  
  if (!formData.pickupTime) {
    errors.pickupTime = 'Pickup time is required';
  }

  // Check if pickup date is not in the past
  if (formData.pickupDate && formData.pickupTime) {
    const selectedDate = new Date(`${formData.pickupDate}T${formData.pickupTime}`);
    const now = new Date();
    if (selectedDate < now) {
      errors.pickupDate = 'Pickup date and time cannot be in the past';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Generate a unique booking ID
 * @returns {string} Unique booking ID
 */
export const generateBookingId = () => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 5);
  return `TB${timestamp}${random}`.toUpperCase();
};

/**
 * Get vehicle display name
 * @param {string} vehicleType - Vehicle type key
 * @returns {string} Display name
 */
export const getVehicleDisplayName = (vehicleType) => {
  const names = {
    sedan: 'SEDAN',
    suv: 'SUV',
    innova: 'INNOVA'
  };
  return names[vehicleType] || vehicleType.toUpperCase();
};

/**
 * Check if a date is today
 * @param {string} date - Date string (YYYY-MM-DD)
 * @returns {boolean} True if date is today
 */
export const isToday = (date) => {
  const today = new Date().toISOString().split('T')[0];
  return date === today;
};

/**
 * Get minimum time for today's booking (current time + 1 hour)
 * @returns {string} Minimum time string (HH:MM)
 */
export const getMinimumTime = () => {
  const now = new Date();
  now.setHours(now.getHours() + 1);
  return now.toTimeString().split(' ')[0].substring(0, 5);
};

/**
 * Convert 24-hour time format to 12-hour format with AM/PM
 * @param {string} time24 - Time in 24-hour format (HH:MM)
 * @returns {string} Time in 12-hour format with AM/PM
 */
export const convertTo12HourFormat = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':');
  const hour24 = parseInt(hours, 10);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  
  return `${hour12}:${minutes} ${ampm}`;
};

/**
 * Convert 12-hour time format to 24-hour format
 * @param {string} time12 - Time in 12-hour format (H:MM AM/PM)
 * @returns {string} Time in 24-hour format (HH:MM)
 */
export const convertTo24HourFormat = (time12) => {
  if (!time12) return '';
  
  // Clean up the input and handle various formats
  const cleanTime = time12.toLowerCase().trim();
  
  // Match patterns like "7:24 pm", "7:24pm", "07:24 PM", etc.
  const timeRegex = /^(\d{1,2}):(\d{2})\s*(am|pm)$/;
  const match = cleanTime.match(timeRegex);
  
  if (!match) return '';
  
  const [, hours, minutes, ampm] = match;
  let hour24 = parseInt(hours, 10);
  
  // Validate hours and minutes
  if (hour24 < 1 || hour24 > 12 || parseInt(minutes, 10) > 59) {
    return '';
  }
  
  if (ampm === 'am' && hour24 === 12) {
    hour24 = 0;
  } else if (ampm === 'pm' && hour24 !== 12) {
    hour24 += 12;
  }
  
  return `${hour24.toString().padStart(2, '0')}:${minutes}`;
};

/**
 * Debounce function to limit API calls
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};